import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import { Line, Doughnut } from 'react-chartjs-2';
import ReactMarkdown from 'react-markdown';
import moment from 'moment';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCog, faCheck } from '@fortawesome/pro-light-svg-icons';

import { SpotlightsPopover } from '../SpotlightsMenus';
import { SkeletonCards, SkeletonLines, Skeleton } from '../SpotlightsSkeleton';
import { getOverview, getRankings, getDailySummary, getDailySummaryDates } from '../../../api/spotlights';
import {
  fmtMoney,
  fmtInt,
  fmtRoas,
  STATUS_LABELS,
  DISPLAY_STATUSES,
  toDisplayLifecycle,
  hue,
  CHART_S,
  CHART_L,
  LIFECYCLE_HUES,
  LIFECYCLE_COLORS
} from '../../../helpers/spotlights_dashboard_helpers';
import { getSmartImage } from '../../../helpers/helpers';
import './SpotlightsOverview.scss';

const WINDOWS = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'lifetime', label: 'Lifetime' }
];
const RANK_METRICS = [
  { key: 'spend', label: 'Spend' },
  { key: 'roas', label: 'AN ROAS' },
  { key: 'orders', label: 'Orders' }
];

// Trend series — Spend keeps the brand green hue; the other two are a balanced triad (≈120°
// apart). Each on its own auto-scaled (hidden) axis so the magnitudes stay comparable.
// Palette (hue/CHART_S/CHART_L/LIFECYCLE_*) is shared via spotlights_dashboard_helpers.
const TREND_SERIES = [
  { key: 'spend', label: 'Spend', color: hue(151), axis: 'y-spend', fill: true, format: fmtMoney },
  { key: 'activeCount', label: 'Active spotlights', color: hue(31), axis: 'y-active', fill: false, format: fmtInt },
  { key: 'orders', label: 'Orders', color: hue(271), axis: 'y-orders', fill: false, format: fmtInt }
];

const SpotlightsOverview = ({ navigate }) => {
  const [window, setWindow] = useState('lifetime');
  // Persisted per-series on/off (key -> hidden). Empty = all visible. Survives window changes.
  const [hiddenSeries, setHiddenSeries] = useState({});
  const [overview, setOverview] = useState(null);
  const [rankings, setRankings] = useState({ spend: [], roas: [], orders: [] });
  const [rankDir, setRankDir] = useState('top'); // 'top' = desc, 'bottom' = asc
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Report A (Performance) + Report B (Needs Attention) digests. The date picker drives both;
  // Report B additionally has a morning/evening edition toggle.
  const [summary, setSummary] = useState(null);
  const [summaryDates, setSummaryDates] = useState([]);
  const [summaryDate, setSummaryDate] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [needsAttention, setNeedsAttention] = useState(null);
  const [naEdition, setNaEdition] = useState('morning');
  const [naLoading, setNaLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setOverview(await getOverview({ window }));
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to load overview.');
    } finally {
      setLoading(false);
    }
  }, [window]);

  useEffect(() => {
    load();
  }, [load]);

  // Rankings load independently of the overview so toggling Top/Bottom (or the window) swaps
  // the lists in place without re-pulsing the whole page.
  useEffect(() => {
    const dir = rankDir === 'bottom' ? 'asc' : 'desc';
    Promise.all([
      getRankings({ metric: 'spend', window, dir, limit: 8 }),
      getRankings({ metric: 'roas', window, dir, limit: 8 }),
      getRankings({ metric: 'orders', window, dir, limit: 8 })
    ])
      .then(([spend, roas, orders]) => setRankings({ spend: spend.rankings, roas: roas.rankings, orders: orders.rankings }))
      .catch(e => globalThis.ALERT?.error(typeof e === 'string' ? e : 'Failed to load brand rankings.'));
  }, [window, rankDir]);

  const loadPerformance = useCallback(date => {
    setSummaryLoading(true);
    getDailySummary({ report: 'performance', date })
      .then(r => setSummary(r.summary))
      .catch(e => globalThis.ALERT?.error(typeof e === 'string' ? e : 'Failed to load the performance digest.'))
      .finally(() => setSummaryLoading(false));
  }, []);

  const loadNeedsAttention = useCallback((date, edition) => {
    setNaLoading(true);
    getDailySummary({ report: 'needs_attention', date, edition })
      .then(r => setNeedsAttention(r.summary))
      .catch(e => globalThis.ALERT?.error(typeof e === 'string' ? e : 'Failed to load the needs-attention digest.'))
      .finally(() => setNaLoading(false));
  }, []);

  useEffect(() => {
    getDailySummaryDates()
      .then(r => setSummaryDates(r.dates || []))
      .catch(e => globalThis.ALERT?.error(typeof e === 'string' ? e : 'Failed to load digest dates.'));
    loadPerformance(null);
    loadNeedsAttention(null, 'morning');
  }, [loadPerformance, loadNeedsAttention]);

  const onPickDate = date => {
    setSummaryDate(date);
    loadPerformance(date);
    loadNeedsAttention(date, naEdition);
  };

  const onPickEdition = edition => {
    setNaEdition(edition);
    // Fall back to the currently-displayed date when the user hasn't explicitly picked one yet,
    // so toggling the edition keeps the same day rather than snapping back to the latest pointer.
    loadNeedsAttention(summaryDate || (summary && summary.date) || null, edition);
  };

  const t = overview?.totals || {};
  const fee = overview?.platformFee || {};
  const att = overview?.attention || {};
  const prev = overview?.previous || null;
  // % change vs the prior equal-length period (null when no prior period / no baseline).
  const delta = (cur, base) => (prev && base > 0 ? (cur - base) / base : null);
  const goToBrands = filter => navigate && navigate('brands', { filter });

  return (
    <div className='spotlights-overview'>
      <div className='sl-controls'>
        <div className='sl-segmented'>
          {WINDOWS.map(w => (
            <div key={w.key} className={cn('sl-segment', { active: window === w.key })} onClick={() => setWindow(w.key)}>
              {w.label}
            </div>
          ))}
        </div>
      </div>

      {error ? (
        <div className='sl-error'>{error}</div>
      ) : !overview ? (
        // First load — pulsing skeletons that match the layout (no spinner flash, no shift).
        <>
          <SkeletonCards count={6} />
          <div className='sl-section'>
            <Skeleton w='160px' h={20} style={{ marginBottom: 18 }} />
            <SkeletonCards count={5} />
          </div>
          <div className='sl-section'>
            <Skeleton w='220px' h={20} style={{ marginBottom: 18 }} />
            <Skeleton w='100%' h={240} r={12} />
          </div>
        </>
      ) : (
        // Subsequent window changes keep the data visible and gently pulse it.
        <div className={cn('sl-refreshable', { 'sl-refreshing': loading })}>
          {/* Metric cards */}
          <div className='sl-card-grid'>
            <Card label='Spend' value={fmtMoney(t.spend)} delta={delta(t.spend, prev?.spend)} />
            <Card label='AN ROAS' value={fmtRoas(t.roasAN)} sub={`${fmtRoas(t.roasSM)} SM ROAS`} delta={delta(t.roasAN, prev?.roasAN)} />
            <Card label='Orders' value={fmtInt(t.orders)} delta={delta(t.orders, prev?.orders)} />
            <Card
              label='Impressions'
              value={fmtInt(t.impressions)}
              sub={`${fmtInt(t.clicks)} clicks`}
              delta={delta(t.impressions, prev?.impressions)}
            />
            <Card label='Live spotlights' value={fmtInt(t.liveCount)} sub={`${fmtInt(t.activeBrandCount)} active brands`} />
            <Card
              label='Revenue (projected)'
              value={fmtMoney(fee.projected)}
              sub={`${fmtMoney(fee.collected)} collected · ${fmtMoney(fee.outstanding)} unpaid`}
            />
          </div>

          {/* Attention — each card deep-links to the Brands tab with the matching filter */}
          <div className='sl-section'>
            <div className='sl-section-title'>Needs attention</div>
            <div className='sl-card-grid'>
              <Card
                label='Pause rec.'
                value={fmtInt(att.pauseRecommended)}
                alert={att.pauseRecommended > 0}
                onClick={() => goToBrands('pauseRec')}
              />
              <Card label='Low ROAS (live)' value={fmtInt(att.lowRoas)} alert={att.lowRoas > 0} onClick={() => goToBrands('lowRoas')} />
              <Card
                label='No spend (live)'
                value={fmtInt(att.noSpendWhileLive)}
                alert={att.noSpendWhileLive > 0}
                onClick={() => goToBrands('noSpend')}
              />
              <Card
                label='Billing paused'
                value={fmtInt(att.billingPausedBrands)}
                alert={att.billingPausedBrands > 0}
                onClick={() => goToBrands('billingPaused')}
              />
            </div>
          </div>

          {/* Trend (left) + lifecycle donut (right) */}
          <div className='sl-overview-row'>
            <div className='sl-section sl-trend'>
              <div className='sl-section-title' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Trend
                <SpotlightsPopover icon={faCog} label='Metrics'>
                  {TREND_SERIES.map(s => {
                    const off = !!hiddenSeries[s.key];
                    return (
                      <button
                        key={s.key}
                        className={cn('sl-metric-toggle', { off })}
                        onClick={() => setHiddenSeries(h => ({ ...h, [s.key]: !h[s.key] }))}
                      >
                        <span className='swatch' style={{ background: s.color }} />
                        <span className='label'>{s.label}</span>
                        {!off && <FontAwesomeIcon icon={faCheck} />}
                      </button>
                    );
                  })}
                </SpotlightsPopover>
              </div>
              {overview.trend && overview.trend.length > 0 ? (
                <Line
                  data={canvas => {
                    // Soft green vertical gradient under the Spend line (matches SpotlightChart).
                    const ctx = canvas.getContext('2d');
                    const gradient = ctx.createLinearGradient(0, 0, 0, 250);
                    gradient.addColorStop(0, `hsla(151, ${CHART_S}%, ${CHART_L}%, 0.30)`);
                    gradient.addColorStop(1, `hsla(151, ${CHART_S}%, ${CHART_L}%, 0.02)`);
                    return {
                      labels: overview.trend.map(d => moment(d.date).format('MMM D')),
                      datasets: TREND_SERIES.map(s => ({
                        label: s.label,
                        data: overview.trend.map(d => d[s.key] || 0),
                        borderColor: s.color,
                        backgroundColor: s.fill ? gradient : s.color,
                        yAxisID: s.axis,
                        fill: s.fill,
                        lineTension: 0,
                        borderWidth: 2,
                        pointRadius: 0,
                        pointHitRadius: 12,
                        pointHoverRadius: 4,
                        pointHoverBackgroundColor: s.color,
                        pointHoverBorderColor: '#fff',
                        pointHoverBorderWidth: 2,
                        hidden: !!hiddenSeries[s.key]
                      }))
                    };
                  }}
                  options={{
                    maintainAspectRatio: false,
                    layout: { padding: { top: 8, right: 4, left: 4 } },
                    legend: { display: false },
                    tooltips: {
                      mode: 'index',
                      intersect: false,
                      backgroundColor: '#fff',
                      titleFontColor: '#171919',
                      bodyFontColor: '#4a4a4a',
                      borderColor: '#ebebeb',
                      borderWidth: 1,
                      cornerRadius: 8,
                      xPadding: 12,
                      yPadding: 10,
                      titleFontFamily: 'DM Sans',
                      bodyFontFamily: 'DM Sans',
                      callbacks: {
                        label: (item, data) => {
                          const s = TREND_SERIES[item.datasetIndex];
                          const val = item.yLabel;
                          return s ? `  ${s.label}: ${s.format(val)}` : `${data.datasets[item.datasetIndex].label}: ${val}`;
                        }
                      }
                    },
                    hover: { mode: 'index', intersect: false },
                    scales: {
                      xAxes: [
                        {
                          gridLines: { display: false, drawBorder: false },
                          ticks: { maxTicksLimit: 8, fontColor: '#9a9a9a', fontFamily: 'DM Sans', padding: 8 }
                        }
                      ],
                      yAxes: [
                        ...TREND_SERIES.map(s => ({ id: s.axis, display: false })),
                        // A labelless grid axis draws the subtle horizontal lines, SpotlightChart-style.
                        {
                          id: 'y-grid',
                          position: 'left',
                          gridLines: { color: 'rgba(0,0,0,0.05)', drawBorder: false, zeroLineColor: 'rgba(0,0,0,0.05)' },
                          ticks: { display: false, maxTicksLimit: 5, beginAtZero: true }
                        }
                      ]
                    }
                  }}
                  height={250}
                />
              ) : (
                <div className='sl-empty'>No trend data.</div>
              )}
            </div>

            {/* Lifecycle breakdown donut */}
            <div className='sl-section sl-lifecycle-card'>
              <div className='sl-section-title'>Lifecycle breakdown</div>
              {(() => {
                const life = toDisplayLifecycle(overview.lifecycle);
                const entries = DISPLAY_STATUSES.filter(s => life[s] > 0).sort((a, b) => (LIFECYCLE_HUES[a] || 0) - (LIFECYCLE_HUES[b] || 0));
                const lifeTotal = entries.reduce((n, s) => n + life[s], 0);
                if (!lifeTotal) return <div className='sl-empty'>No spotlights yet.</div>;
                return (
                  <div className='sl-donut-wrap'>
                    <Doughnut
                      data={{
                        labels: entries.map(s => STATUS_LABELS[s]),
                        datasets: [
                          {
                            data: entries.map(s => life[s]),
                            backgroundColor: entries.map(s => LIFECYCLE_COLORS[s] || '#c7c7c7'),
                            borderColor: '#fff',
                            borderWidth: 2
                          }
                        ]
                      }}
                      options={{
                        maintainAspectRatio: false,
                        cutoutPercentage: 68,
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: { usePointStyle: true, boxWidth: 8, padding: 10, fontColor: '#767676', fontFamily: 'DM Sans', fontSize: 11 }
                        },
                        tooltips: {
                          backgroundColor: '#fff',
                          titleFontColor: '#171919',
                          bodyFontColor: '#4a4a4a',
                          borderColor: '#ebebeb',
                          borderWidth: 1,
                          cornerRadius: 8,
                          callbacks: {
                            label: (item, data) => {
                              const label = data.labels[item.index];
                              const val = data.datasets[0].data[item.index];
                              const pct = lifeTotal ? Math.round((val / lifeTotal) * 100) : 0;
                              return `  ${label}: ${val} (${pct}%)`;
                            }
                          }
                        }
                      }}
                      height={190}
                    />
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Rankings — Top / Bottom toggle */}
          <div className='sl-section'>
            <div className='sl-section-title'>
              <div className='sl-segmented'>
                <div className={cn('sl-segment', { active: rankDir === 'top' })} onClick={() => setRankDir('top')}>
                  Top brands
                </div>
                <div className={cn('sl-segment', { active: rankDir === 'bottom' })} onClick={() => setRankDir('bottom')}>
                  Bottom brands
                </div>
              </div>
            </div>
            <div className='sl-card-grid' style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
              {RANK_METRICS.map(m => (
                <RankingList key={m.key} title={m.label} rows={rankings[m.key]} metric={m.key} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Daily digests — independent of the window toggle. Date picker drives both reports. */}
      <div className='sl-section'>
        <div className='sl-section-title' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Daily digests</span>
          {summaryDates.length > 0 && (
            <select className='sl-select' value={summaryDate || (summary && summary.date) || ''} onChange={e => onPickDate(e.target.value)}>
              {summaryDates.map(d => (
                <option key={d} value={d}>
                  {moment(d).format('MMM D, YYYY')}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Report A — Performance overview */}
        <div className='sl-section-title' style={{ fontSize: 14, marginTop: 8 }}>📊 Performance</div>
        {summaryLoading ? (
          <SkeletonLines count={6} />
        ) : summary ? (
          <>
            <div className='sl-markdown'>
              <ReactMarkdown>{summary.parentMarkdown || summary.markdown}</ReactMarkdown>
            </div>
            {(summary.brands || []).length > 0 && (
              <div className='sl-digest-threads'>
                {summary.brands.map(b => (
                  <DigestThread key={b.Brand_id || b.brandName} brand={b} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className='sl-empty'>No performance report yet — cached when the morning job runs.</div>
        )}

        {/* Report B — Needs Attention, with morning/evening edition toggle */}
        <div className='sl-section-title' style={{ fontSize: 14, marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>🚨 Needs attention</span>
          <div className='sl-segmented'>
            {['morning', 'evening'].map(ed => (
              <div key={ed} className={cn('sl-segment', { active: naEdition === ed })} onClick={() => onPickEdition(ed)}>
                {ed === 'morning' ? 'Morning' : 'Evening'}
              </div>
            ))}
          </div>
        </div>
        {naLoading ? (
          <SkeletonLines count={5} />
        ) : needsAttention ? (
          <div className='sl-markdown'>
            <ReactMarkdown>{needsAttention.parentMarkdown || needsAttention.markdown}</ReactMarkdown>
          </div>
        ) : (
          <div className='sl-empty'>No {naEdition} needs-attention report for this date yet.</div>
        )}
      </div>
    </div>
  );
};

const Card = ({ label, value, sub, alert, delta, onClick }) => {
  const hasDelta = delta != null && isFinite(delta);
  return (
    <div className={cn('sl-card', { alert, clickable: !!onClick })} onClick={onClick}>
      {onClick && <FontAwesomeIcon icon={faArrowRight} className='sl-card-go' />}
      <div className='sl-card-label'>{label}</div>
      <div className='sl-card-value'>{value}</div>
      {hasDelta && (
        <div className={cn('sl-card-delta', { up: delta > 0, down: delta < 0 })}>
          {delta > 0 ? '▲' : delta < 0 ? '▼' : '–'} {Math.abs(delta * 100).toFixed(0)}% vs prev
        </div>
      )}
      {sub && <div className='sl-card-sub'>{sub}</div>}
    </div>
  );
};

// A collapsible per-brand digest "thread" (mirrors a Slack threaded reply).
const DigestThread = ({ brand }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn('sl-digest-thread', { open })}>
      <div className='sl-digest-thread-head' onClick={() => setOpen(o => !o)}>
        <span className={cn('sl-caret', { open })}>▸</span>
        <span className='sl-digest-thread-name'>{brand.brandName}</span>
        <span className='sl-digest-thread-summary'>{brand.summary}</span>
        {brand.attentionCount > 0 && <span className='sl-pill amber'>{brand.attentionCount} need attention</span>}
      </div>
      {open && (
        <div className='sl-markdown sl-digest-thread-body'>
          <ReactMarkdown>{brand.markdown}</ReactMarkdown>
        </div>
      )}
    </div>
  );
};

const RankingList = ({ title, rows, metric }) => {
  const valueFor = r => (metric === 'spend' ? fmtMoney(r.spend) : metric === 'roas' ? fmtRoas(r.roasAN) : fmtInt(r.orders));
  return (
    <div className='sl-card'>
      <div className='sl-card-label'>By {title}</div>
      <table className='sl-table' style={{ marginTop: 8 }}>
        <tbody>
          {(rows || []).map((r, i) => (
            <tr key={r.Brand_id}>
              <td style={{ width: 18, color: '#9a9a9a' }}>{i + 1}</td>
              <td>
                <div className='sl-brand-cell'>
                  {r.brandLogo && <img src={getSmartImage(r.brandLogo)} alt='' />}
                  {r.brandName}
                </div>
              </td>
              <td className='num'>{valueFor(r)}</td>
            </tr>
          ))}
          {(!rows || !rows.length) && (
            <tr>
              <td colSpan={3} className='sl-empty' style={{ padding: 16 }}>
                No data
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SpotlightsOverview;
